import { useEffect, useState } from 'react'
import { parseAsBoolean, useQueryState } from 'nuqs'

import { useFeedbackPageUrl } from '../components/FeedbackPageUrlContext'

export const FeedbackDialogQueryParamKey = 'feedback'
export const ProposeChangeDialogQueryParamKey = 'proposeChange'

export interface ScreenshotCapture {
  blob: Blob
  objectUrl: string
  pageUrl: string
}

type FeedbackScreenshotState =
  | { status: 'empty' }
  | { screenshot: ScreenshotCapture; status: 'captured' }

const EmptyFeedbackScreenshotState: FeedbackScreenshotState = {
  status: 'empty',
}

export interface UserFeedbackDialogController {
  clearScreenshot: () => void
  closeFeedbackDialog: () => void
  isDialogOpen: boolean
  openFeedbackDialog: () => void
  screenshot: ScreenshotCapture | null
  setScreenshotFromFile: (screenshotFile: File) => void
}

function useFeedbackDialogController({
  isDialogOpen,
  setDialogOpen,
}: {
  isDialogOpen: boolean
  setDialogOpen: (open: boolean) => void
}): UserFeedbackDialogController {
  const feedbackPageUrl = useFeedbackPageUrl()
  const [screenshotState, setScreenshotState] =
    useState<FeedbackScreenshotState>(EmptyFeedbackScreenshotState)
  const screenshot =
    screenshotState.status === 'captured' ? screenshotState.screenshot : null

  const clearScreenshot = () => {
    setScreenshotState((currentScreenshotState) => {
      revokeCapturedScreenshotObjectUrl(currentScreenshotState)
      return EmptyFeedbackScreenshotState
    })
  }

  useEffect(() => {
    if (!isDialogOpen) {
      return
    }

    return () => {
      setScreenshotState((currentScreenshotState) => {
        revokeCapturedScreenshotObjectUrl(currentScreenshotState)
        return EmptyFeedbackScreenshotState
      })
    }
  }, [isDialogOpen])

  const openFeedbackDialog = () => {
    setDialogOpen(true)
  }

  const closeFeedbackDialog = () => {
    clearScreenshot()
    setDialogOpen(false)
  }

  const setScreenshotFromFile = (screenshotFile: File) => {
    setScreenshotState((currentScreenshotState) => {
      revokeCapturedScreenshotObjectUrl(currentScreenshotState)
      return {
        screenshot: {
          blob: screenshotFile,
          objectUrl: window.URL.createObjectURL(screenshotFile),
          pageUrl: feedbackPageUrl ?? window.location.href,
        },
        status: 'captured',
      }
    })
  }

  return {
    clearScreenshot,
    closeFeedbackDialog,
    isDialogOpen,
    openFeedbackDialog,
    screenshot,
    setScreenshotFromFile,
  }
}

export function useUserFeedbackDialog(): UserFeedbackDialogController {
  const [isDialogOpen, setDialogOpen] = useQueryState(
    FeedbackDialogQueryParamKey,
    parseAsBoolean.withDefault(false),
  )

  return useFeedbackDialogController({
    isDialogOpen,
    setDialogOpen: (open) => {
      void setDialogOpen(open)
    },
  })
}

export function useLocalUserFeedbackDialog(): UserFeedbackDialogController {
  const [isDialogOpen, setDialogOpen] = useState(false)

  return useFeedbackDialogController({
    isDialogOpen,
    setDialogOpen,
  })
}

export function useProposeChangeDialog(): UserFeedbackDialogController {
  const [isDialogOpen, setDialogOpen] = useQueryState(
    ProposeChangeDialogQueryParamKey,
    parseAsBoolean.withDefault(false),
  )

  return useFeedbackDialogController({
    isDialogOpen,
    setDialogOpen: (open) => {
      void setDialogOpen(open)
    },
  })
}

export function stripDialogQueryParams(pageUrl: string): string {
  const url = new URL(pageUrl)
  url.searchParams.delete(FeedbackDialogQueryParamKey)
  url.searchParams.delete(ProposeChangeDialogQueryParamKey)
  return url.toString()
}

function revokeCapturedScreenshotObjectUrl(
  screenshotState: FeedbackScreenshotState,
) {
  if (screenshotState.status !== 'captured') {
    return
  }

  window.URL.revokeObjectURL(screenshotState.screenshot.objectUrl)
}
